import goalDefinitions from './goals.json'
import { ANALYTICS_CONFIG } from './config'
import { installInternalAnalytics, recordGoalInternal, recordPageView, recordEcommerce } from './collector'
import { hasAnalyticsConsent, LEGAL_CONSENT_EVENT } from '@/legal/consent'

const GOAL_IDS = new Set(goalDefinitions.map(item => item.id))
const PRIVATE_KEYS = /email|phone|name|fio|message|comment|address|hash|token|password/i
const queue = []
const recent = new Map()
const once = new Set()
let installed = false
let counterReady = false
let previousUrl = ''
let cardObserver = null
let mutationObserver = null

function isYandexProduction() {
  return typeof window !== 'undefined'
    && window.location.protocol === 'https:'
    && window.location.hostname === 'druzhba-mebel.ru'
}

function counterId() {
  if (!isYandexProduction() || !hasAnalyticsConsent()) return 0
  return Number(ANALYTICS_CONFIG.metricaCounterId || 0)
}

function debug(...args) {
  if (ANALYTICS_CONFIG.metricaDebug && typeof console !== 'undefined') console.info('[METRICA]', ...args)
}

function deviceType() {
  const width = window.innerWidth || 0
  if (width <= 680) return 'mobile'
  if (width <= 1180) return 'tablet'
  return 'desktop'
}

function safePath() {
  return window.location.pathname || '/'
}

function safeValue(value, depth = 0) {
  if (depth > 4) return undefined
  if (value === null || value === undefined) return value
  if (typeof value === 'boolean' || typeof value === 'number') return value
  if (typeof value === 'string') return value.slice(0, 180)
  if (Array.isArray(value)) return value.slice(0, 20).map(item => safeValue(item, depth + 1)).filter(item => item !== undefined)
  if (typeof value === 'object') {
    return Object.keys(value).slice(0, 40).reduce((acc, key) => {
      if (PRIVATE_KEYS.test(key)) return acc
      const next = safeValue(value[key], depth + 1)
      if (next !== undefined) acc[key] = next
      return acc
    }, {})
  }
  return String(value).slice(0, 180)
}

function commonParams(extra = {}) {
  return safeValue({
    page: { path: safePath(), title: document.title.slice(0, 120) },
    device: { type: deviceType(), width: window.innerWidth || 0 },
    auth: { state: window.localStorage.getItem('user_hash') ? 'authorized' : 'guest' },
    ...extra
  })
}

function ensureYmQueue() {
  if (typeof window.ym === 'function') return
  window.ym = function ym() { (window.ym.a = window.ym.a || []).push(arguments) }
  window.ym.l = Date.now()
}

function loadCounterScript() {
  if (document.querySelector('script[data-druzhba-metrica]')) return
  const script = document.createElement('script')
  script.async = true
  script.src = 'https://mc.yandex.ru/metrika/tag.js'
  script.dataset.druzhbaMetrica = '1'
  script.onerror = () => debug('Не загрузился tag.js')
  document.head.appendChild(script)
}

function flushQueue() {
  if (!counterReady) return
  while (queue.length) {
    const item = queue.shift()
    sendGoal(item.id, item.params, true)
  }
}

function sendGoal(id, params = {}, fromQueue = false) {
  if (!GOAL_IDS.has(id)) {
    debug('Неизвестная цель', id)
    return false
  }

  const now = Date.now()
  const last = recent.get(id) || 0
  if (!fromQueue && now - last < 1000) return false
  if (!fromQueue) {
    recent.set(id, now)
    const definition = goalDefinitions.find(item => item.id === id)
    if (definition) recordGoalInternal(definition, params)
  }

  const cid = counterId()
  if (!cid) return true
  if (!counterReady || typeof window.ym !== 'function') {
    if (!fromQueue) queue.push({ id, params })
    return true
  }

  const payload = commonParams(params)
  window.ym(cid, 'reachGoal', id, payload)
  debug('goal', id, payload)
  return true
}

export function trackGoal(id, params = {}) {
  return sendGoal(id, params)
}

export function trackGoalOnce(id, key = id, params = {}) {
  const token = `${id}:${key}`
  if (once.has(token)) return false
  once.add(token)
  return trackGoal(id, params)
}

export function trackError(source, error, extra = {}) {
  const message = error && error.message ? String(error.message) : String(error || 'unknown')
  trackGoal('site_error', { source, error_type: error && error.name ? error.name : 'Error', error_message: message.slice(0, 140), ...extra })
}

export function ecommerceProduct(item, extra = {}) {
  if (!item) return null
  const variant = item.choosen_color && (item.choosen_color.title || item.choosen_color.color)
    ? (item.choosen_color.title || item.choosen_color.color)
    : item.selectedColor || ''
  return {
    id: String(item.id || item.equipment_id || ''),
    name: String(item.name || 'Диван'),
    brand: 'Дружба',
    category: String(item.contruction || item.category || 'Мебель / Диваны'),
    price: Number(item.price || 0),
    quantity: Number(item.count || item.quantity || 1),
    variant: String(variant || ''),
    list: String(extra.list || item.list || 'Сайт'),
    position: Number(extra.position || item.position || 0) || undefined
  }
}

export function pushEcommerce(action, products, actionField = null) {
  const cleanProducts = (Array.isArray(products) ? products : [products]).filter(Boolean).map(item => ecommerceProduct(item)).filter(Boolean)
  if (!cleanProducts.length && !actionField) return
  cleanProducts.forEach(product => recordEcommerce(action, product, actionField || {}))
  if (!cleanProducts.length && action === 'purchase') recordEcommerce(action, {}, actionField || {})

  // Яндекс ecommerce отправляем только с единственного боевого домена.
  // localhost/dev/test-копии не должны попадать в «Количество покупок» Метрики.
  if (!isYandexProduction()) return

  window.dataLayer = window.dataLayer || []
  const body = { products: cleanProducts }
  if (action === 'purchase') body.actionField = actionField || {}
  window.dataLayer.push({ ecommerce: { currencyCode: ANALYTICS_CONFIG.currency, [action]: body } })
  debug('ecommerce', action, body)
}

function routeProduct(store, route) {
  if (!route || route.name !== 'catalog-equipment') return null
  const list = Array.isArray(store.getters.equipment) ? store.getters.equipment : []
  return list.find(item => String(item.id) === String(route.params.id)) || null
}

function sendHit(route, store) {
  if (!route) return
  recordPageView(route)
  const cid = counterId()
  if (!cid || !counterReady) return
  const url = `${window.location.origin}${route.fullPath || route.path || '/'}`
  window.ym(cid, 'hit', url, {
    title: document.title,
    referer: previousUrl || document.referrer || '',
    params: commonParams({ route: { name: route.name || '', path: route.path || '/' } })
  })
  previousUrl = url

  if (route.path === '/') {
    trackGoal('catalog_view')
    const products = (store.getters.equipment || []).slice(0, 40).map((item, index) => ecommerceProduct(item, { list: 'Каталог', position: index + 1 }))
    if (products.length) {
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({ ecommerce: { currencyCode: ANALYTICS_CONFIG.currency, impressions: products } })
    }
  }
  if (route.name === 'catalog-equipment') {
    const product = routeProduct(store, route)
    trackGoal('product_view', product ? { product: { id: product.id, price: Number(product.price || 0) } } : {})
    if (product) pushEcommerce('detail', [product])
  }
  if (route.path === '/cart') {
    const cart = store.getters.cart || []
    trackGoal(cart.length ? 'cart_view' : 'cart_empty', { cart: { items: cart.length } })
  }
  if (route.path === '/login') trackGoal('login_view')
  if (route.path === '/register') trackGoal('register_view')
  if (route.path === '/profile') trackGoal('profile_view')
}

function textOf(node) {
  return String(node && node.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 120)
}

function closest(target, selector) {
  return target && target.closest ? target.closest(selector) : null
}

function productContext(node) {
  const card = closest(node, '.sofa-card')
  if (!card) return {}
  const name = textOf(card.querySelector('h2'))
  const price = textOf(card.querySelector('.sofa-card__price strong')).replace(/\D/g, '')
  return { product: { name, price: Number(price || 0) } }
}

function installClickTracking() {
  document.addEventListener('click', event => {
    const target = event.target
    let node

    if ((node = closest(target, '.sofa-brand,.sofa-mobilebar__brand'))) trackGoal('logo_click')
    else if ((node = closest(target, '.sofa-catalog-button'))) trackGoal('catalog_menu_open')
    else if ((node = closest(target, '.sofa-mobilebar__menu'))) trackGoal('mobile_menu_open')
    else if ((node = closest(target, '.sofa-category-nav button,.sofa-mobile-catalog__grid button,.sofa-quick-links button,.site-footer__nav button'))) trackGoal('nav_category_click', { label: textOf(node) })
    else if ((node = closest(target, '.sofa-utility__button--city'))) trackGoal('city_open')
    else if ((node = closest(target, '.sofa-city-list button'))) trackGoal('city_select', { city: textOf(node).replace('✓','').trim() })
    else if ((node = closest(target, 'a[href^="tel:"]'))) trackGoal('phone_click', { placement: closest(node,'.site-footer') ? 'footer' : 'site' })
    else if ((node = closest(target, 'a[href^="mailto:"]'))) trackGoal('email_click', { placement: closest(node,'.site-footer') ? 'footer' : 'site' })
    else if ((node = closest(target, 'a[href*="vk.com"]'))) trackGoal('vk_click')
    else if ((node = closest(target, 'a[href*="t.me"]'))) trackGoal('telegram_click')
    else if ((node = closest(target, '.sofa-mainbar__action[aria-label="Профиль"],.sofa-mobilebar__profile'))) trackGoal('header_profile_click')
    else if ((node = closest(target, '.sofa-mainbar__action--favorite,.sofa-mobilebar__favorite'))) trackGoal('header_favorites_click')
    else if ((node = closest(target, '.sofa-mainbar__action[aria-label="Корзина"],.sofa-mobilebar__cart'))) trackGoal('header_cart_click')

    if ((node = closest(target, '.sofa-filter-main'))) trackGoal('catalog_filter_open')
    if ((node = closest(target, '.sofa-drawer__apply'))) trackGoal('catalog_filter_apply', { label: textOf(node) })
    if ((node = closest(target, '.sofa-drawer__reset,.sofa-empty button'))) trackGoal('catalog_filter_reset')
    if ((node = closest(target, '.sofa-filter-chip'))) trackGoal('catalog_type_select', { filter: textOf(node) })
    if ((node = closest(target, '.sofa-card__colors button'))) {
      trackGoal('catalog_color_select', { color: node.getAttribute('aria-label') || node.title || '' })
      trackGoal('product_card_color', productContext(node))
    }
    if ((node = closest(target, '.sofa-card__visual img'))) trackGoal('product_card_image', productContext(node))
    if ((node = closest(target, '.sofa-card__visual,.sofa-card h2'))) trackGoal('product_card_click', productContext(node))
    if ((node = closest(target, '.product-gallery__main'))) trackGoal('product_gallery_open')
    if ((node = closest(target, '.product-gallery__thumbs button'))) {
      const buttons = Array.from(node.parentNode.querySelectorAll('button'))
      const index = buttons.indexOf(node)
      const active = buttons.findIndex(button => button.classList.contains('is-active'))
      trackGoal(index >= active ? 'product_gallery_next' : 'product_gallery_prev', { image_index: index + 1 })
    }
    if ((node = closest(target, '.product-colors__list button'))) trackGoal('product_variant_select', { variant: node.getAttribute('aria-label') || node.title || textOf(node) })
    if ((node = closest(target, '.product-details-toggle'))) trackGoal('product_specs_expand')
    if ((node = closest(target, '.reviews-small button,.review-small,.reviews-card'))) trackGoal('product_reviews_click')
    if ((node = closest(target, '.same-equipment button,.same-equipment__card,.related-products a,.related-products button'))) trackGoal('related_product_click', { label: textOf(node) })
    if ((node = closest(target, '.product-cart__add')) && /в корзину/i.test(textOf(node))) trackGoal('product_buy_click')
    if ((node = closest(target, '.product-question,.product-consultation,.product-lead'))) trackGoal('product_question_click')

    if ((node = closest(target, '.cart-list__counter-right'))) trackGoal('cart_item_plus')
    if ((node = closest(target, '.cart-list__counter-left'))) trackGoal('cart_item_minus')
    if ((node = closest(target, '.cart-list__trash-img'))) trackGoal('cart_item_remove')
    if ((node = closest(target, '.cart-list__pattern,.cart-list__color-p'))) trackGoal('cart_color_change')
    if ((node = closest(target, '.cart-list__address-method--add'))) trackGoal('address_add_open')
    if ((node = closest(target, '.cart-list__address-method:not(.cart-list__address-method--add)'))) trackGoal('address_select', { label: textOf(node) })
    if ((node = closest(target, '.cart-list__address-tabs button'))) trackGoal(/самовывоз/i.test(textOf(node)) ? 'delivery_pickup' : 'delivery_shipping')
    if ((node = closest(target, '.payment__method'))) trackGoal(/получении/i.test(textOf(node)) ? 'payment_cash' : 'payment_online')
    if ((node = closest(target, '.cart-order__button'))) trackGoal('checkout_submit', { label: textOf(node) })
    if ((node = closest(target, '.profile-orders .order,.order-card,.orders-history__item'))) trackGoal('profile_order_open', { label: textOf(node) })
    if ((node = closest(target, '.news-card,.news-hero__visual button'))) trackGoal('news_article_open', { label: textOf(node) })
    if ((node = closest(target, '.contact__button,.contact__button-container'))) trackGoal('contact_form_submit')
  }, true)

  document.addEventListener('focusin', event => {
    const target = event.target
    if (target && target.matches && target.matches('input[type="search"]')) trackGoalOnce('search_focus', safePath())
    if (closest(target, '.contact__container')) trackGoalOnce('contact_form_start', safePath())
    if (closest(target, '.cart-list__container,.cart-order__container')) trackGoalOnce('checkout_start', safePath())
  }, true)

  document.addEventListener('keydown', event => {
    if (event.key === 'Enter' && event.target && event.target.matches && event.target.matches('input[type="search"]')) {
      trackGoal('search_submit', { query_length: String(event.target.value || '').length })
    }
  }, true)

  document.addEventListener('change', event => {
    const target = event.target
    if (closest(target, '.sofa-price-inputs')) trackGoal('catalog_price_change')
    if (closest(target, '.sofa-checkbox')) trackGoal('catalog_type_select', { checked: Boolean(target.checked), value: String(target.value || '') })
    if (closest(target, '.catalog__filter-checkbox') && target.type === 'checkbox' && target.checked) trackGoal('terms_accept')
  }, true)
}

function observeProductCards() {
  if (!('IntersectionObserver' in window)) return
  if (cardObserver) cardObserver.disconnect()
  cardObserver = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting || entry.intersectionRatio < 0.55) return
      const card = entry.target
      const name = textOf(card.querySelector('h2')) || card.dataset.productId || 'product'
      trackGoalOnce('product_card_view', name, productContext(card))
      cardObserver.unobserve(card)
    })
  }, { threshold: [0.55] })
  document.querySelectorAll('.sofa-card').forEach(card => cardObserver.observe(card))
}

function installMutationObserver() {
  observeProductCards()
  mutationObserver = new MutationObserver(() => observeProductCards())
  mutationObserver.observe(document.getElementById('app') || document.body, { childList: true, subtree: true })
}

function installEngagement() {
  let interacted = false
  const first = event => {
    if (interacted) return
    interacted = true
    trackGoal('first_interaction', { type: event.type })
    ;['click','touchstart','keydown'].forEach(name => document.removeEventListener(name, first, true))
  }
  ;['click','touchstart','keydown'].forEach(name => document.addEventListener(name, first, true))
  window.setTimeout(() => trackGoal('engaged_30s'), 30000)
  window.setTimeout(() => trackGoal('engaged_60s'), 60000)

  const thresholds = [25,50,75,90]
  const reached = new Set()
  const onScroll = () => {
    const height = Math.max(document.documentElement.scrollHeight - window.innerHeight, 1)
    const percent = Math.round((window.scrollY / height) * 100)
    thresholds.forEach(value => {
      if (percent >= value && !reached.has(value)) {
        reached.add(value)
        trackGoal(`scroll_${value}`)
      }
    })
  }
  window.addEventListener('scroll', onScroll, { passive: true })
}

function installErrors() {
  window.addEventListener('error', event => trackError('window', event.error || event.message))
  window.addEventListener('unhandledrejection', event => trackError('promise', event.reason))
}

function installStoreTracking(store, router) {
  if (!store || typeof store.subscribeAction !== 'function') return
  const favoriteBefore = new Map()
  store.subscribeAction({
    before(action) {
      const type = action.type
      if (type === 'TOGGLE_FAVORITE') favoriteBefore.set(String(action.payload), Boolean(store.getters.isFavorite && store.getters.isFavorite(action.payload)))
      if (type === 'LOGIN_USER') trackGoal('login_submit')
      if (type === 'REGISTER_USER') trackGoal('register_submit')
      if (type === 'SEND_REQUEST') trackGoal('contact_form_submit')
      if (type === 'ADD_REVIEW') trackGoal('review_form_submit')
      if (type === 'CREATE_ORDER') {
        trackGoal('checkout_submit')
        const cart = store.getters.cart || []
        pushEcommerce('detail', cart)
      }
    },
    after(action, state) {
      const type = action.type
      const payload = action.payload
      if (type === 'ADD_TO_CART') {
        const onProduct = router.currentRoute && router.currentRoute.name === 'catalog-equipment'
        trackGoal(onProduct ? 'add_cart_product' : 'add_cart_card', { product: { id: payload && payload.id, price: Number(payload && payload.price || 0) } })
        pushEcommerce('add', [payload])
      }
      if (type === 'DELETE_FROM_CART' || type === 'DELETE_GROUP_FROM_CART') pushEcommerce('remove', [payload])
      if (type === 'TOGGLE_FAVORITE') {
        const was = favoriteBefore.get(String(payload))
        trackGoal(was ? 'product_favorite_remove' : 'product_favorite_add', { product: { id: payload } })
        favoriteBefore.delete(String(payload))
      }
      if (type === 'ADD_ADDRESS') trackGoal('address_add_submit')
      if (type === 'DELETE_ADDRESS') trackGoal('address_delete')
      if (type === 'GET_SHIPPING_QUOTES') {
        const quotes = store.getters.shippingQuotes || store.getters.shipping_quotes || []
        trackGoal(Array.isArray(quotes) && quotes.length ? 'shipping_quote_ok' : 'shipping_quote_error')
      }
      if (type === 'LOGIN_USER') {
        const ok = Boolean(store.getters.user && store.getters.user.email)
        trackGoal(ok ? 'login_success' : 'login_error')
      }
      if (type === 'REGISTER_USER') {
        const feedback = String(store.getters.authFeedback || '')
        trackGoal(/создан|отправлено|подтверд/i.test(feedback) ? 'register_success' : 'register_error')
      }
      if (type === 'SEND_REQUEST') trackGoal('contact_form_success')
      // order_created is sent only by checkout/payment components after the server confirms an order.
    },
    error(action, state, error) {
      const type = action.type
      if (type === 'LOGIN_USER') trackGoal('login_error')
      else if (type === 'REGISTER_USER') trackGoal('register_error')
      else if (type === 'CREATE_ORDER') trackGoal('checkout_api_error')
      else if (type === 'GET_SHIPPING_QUOTES') trackGoal('shipping_quote_error')
      else if (type === 'SEND_REQUEST') trackError('contact_form', error)
      else trackError(`store:${type}`, error)
    }
  })
}

function activateYandexCounter() {
  const cid = counterId()
  if (!cid || counterReady) return false
  ensureYmQueue()
  loadCounterScript()
  window.ym(cid, 'init', {
    defer: true,
    clickmap: true,
    trackLinks: true,
    accurateTrackBounce: true,
    webvisor: true,
    ecommerce: ANALYTICS_CONFIG.ecommerceContainer
  })
  counterReady = true
  if (window.__DRUZHBA_METRICA__) {
    window.__DRUZHBA_METRICA__.counterId = cid
    window.__DRUZHBA_METRICA__.ready = true
  }
  flushQueue()
  return true
}

export function installDruzhbaMetrica({ router, store }) {
  if (installed) return
  installed = true
  installInternalAnalytics()
  window.dataLayer = window.dataLayer || []
  window.__DRUZHBA_METRICA__ = {
    installed: true,
    counterId: counterId(),
    goals: goalDefinitions.length,
    ready: false,
    consentRequired: true,
    testGoal: id => trackGoal(id || 'app_loaded', { test: true })
  }

  if (hasAnalyticsConsent()) activateYandexCounter()
  else {
    debug('Аналитика ждёт согласия пользователя')
    window.addEventListener(LEGAL_CONSENT_EVENT, () => {
      installInternalAnalytics()
      if (hasAnalyticsConsent()) activateYandexCounter()
    })
  }

  trackGoal('app_loaded', { version: 'legal-consent-v1' })
  installClickTracking()
  installEngagement()
  installErrors()
  installMutationObserver()
  installStoreTracking(store, router)

  if (router) {
    router.afterEach((to, from) => {
      window.setTimeout(() => {
        trackGoal('route_change', { route: { from: from && from.path || '', to: to.path, name: to.name || '' } })
        sendHit(to, store)
      }, 0)
    })
    router.onReady(() => window.setTimeout(() => sendHit(router.currentRoute, store), 0))
  }
}

export { goalDefinitions }
